import {
  ClipboardEvent,
  ComponentProps,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useControlledValue } from "../../hooks/useControlledValue";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { Input } from "../Input";
import { CodeInputProps, CodeInputType } from "./types";

export const CodeInput = (props: CodeInputProps) => {
  const {
    length = 6,
    value = "",
    onChange,
    onComplete,
    inputType = "numeric",
    invalid = false,
    autoFocus = true,
    disabled = false,
    enableAutoSubmitOnComplete = true,
    className,
    ...rest
  } = props;

  const { value: codeValue, onChange: handleValueChange } = useControlledValue({
    controlledValue: value,
    controlledOnChange: (value) => onChange?.(value as string),
    defaultValue: "",
  });

  useEffect(() => {
    if (!codeValue || codeValue.length === 0) {
      focusInput(0);
    }
  }, [codeValue]);

  const patternMap: Record<CodeInputType, string> = {
    numeric: "[0-9]*",
    alpha: "[A-Za-z]*",
    alphanumeric: "[A-Za-z0-9]*",
  };

  const pattern = patternMap[inputType];

  const stringToArray = useCallback(
    (str: string): string[] => {
      const chars = str.split("").slice(0, length);
      return Array.from({ length }, (_, i) => chars[i] ?? "");
    },
    [length],
  );

  const codeValueString = codeValue ?? "";

  const internalCode = useMemo(
    () => stringToArray(codeValueString),
    [codeValueString, stringToArray],
  );

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { t, rtl } = useTranslation();

  const createInputRef = useCallback(
    (index: number): ComponentProps<typeof Input>["ref"] => {
      return ((el: HTMLInputElement | null) => {
        inputRefs.current[index] = el;
      }) as ComponentProps<typeof Input>["ref"];
    },
    [],
  );

  const validateInput = useCallback(
    (value: string): string => {
      const negatedPattern = pattern.replace(/\[(.*?)\]\*?$/, "[^$1]");
      return value.replace(new RegExp(negatedPattern, "g"), "");
    },
    [pattern],
  );

  const focusInput = useCallback((index: number) => {
    inputRefs.current[index]?.focus();
  }, []);

  const getNextIndexForArrowKeys = useCallback(
    (currentIndex: number, direction: "left" | "right") => {
      const isLeft = direction === "left";
      const delta = isLeft === rtl ? 1 : -1;
      const nextIndex = currentIndex + delta;
      return Math.max(0, Math.min(length - 1, nextIndex));
    },
    [length, rtl],
  );

  const handleBackspaceDeleteKeys = useCallback(
    (currentCode: string[], index: number) => {
      const newCode = [...currentCode];

      if (currentCode[index] && currentCode[index] !== " ") {
        newCode[index] = "";
        return newCode;
      }

      if (index > 0) {
        newCode[index - 1] = "";
        return newCode;
      }

      return null;
    },
    [],
  );

  const updateCode = useCallback(
    (newCode: string[]) => {
      const codeWithSpaces = newCode.map((c) => (c === "" ? " " : c));
      const codeString = codeWithSpaces.join("");
      handleValueChange(codeString);

      const isComplete = codeString.replaceAll(" ", "").length === length;
      if (isComplete && onComplete && enableAutoSubmitOnComplete) {
        onComplete(codeString);
      }
    },
    [handleValueChange, onComplete, length, enableAutoSubmitOnComplete],
  );

  const handleChange = useCallback(
    (index: number, value: string) => {
      let inputValue = value;

      if (inputValue) {
        inputValue = validateInput(inputValue);

        if (!inputValue) return;
      }

      const newCode = [...internalCode];
      newCode[index] = inputValue;
      updateCode(newCode);

      if (inputValue && index < length - 1) {
        focusInput(index + 1);
      }
    },
    [internalCode, length, updateCode, validateInput, focusInput],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>, index: number) => {
      if (disabled) return;

      if (e.key === "Backspace" || e.key === "Delete") {
        const newCode = handleBackspaceDeleteKeys(internalCode, index);
        if (newCode) {
          e.preventDefault();
          updateCode(newCode);
          if (
            !internalCode[index] ||
            (internalCode[index] === " " && index > 0)
          ) {
            focusInput(index - 1);
          }
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const nextIndex = getNextIndexForArrowKeys(index, "left");
        if (nextIndex !== index) {
          focusInput(nextIndex);
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        const nextIndex = getNextIndexForArrowKeys(index, "right");
        if (nextIndex !== index) {
          focusInput(nextIndex);
        }
      }
    },
    [
      internalCode,
      disabled,
      updateCode,
      handleBackspaceDeleteKeys,
      getNextIndexForArrowKeys,
      focusInput,
    ],
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>, index: number) => {
      e.preventDefault();

      const pastedText = e.clipboardData.getData("text");
      let validChars = validateInput(pastedText);

      validChars = validChars.slice(0, length);

      if (validChars.length > 0) {
        const newCode = [...internalCode];

        for (let i = 0; i < validChars.length && index + i < length; i++) {
          newCode[index + i] = validChars[i];
        }

        updateCode(newCode);

        const nextIndex = Math.min(index + validChars.length, length - 1);
        focusInput(nextIndex);
      }
    },
    [internalCode, length, updateCode, validateInput, focusInput],
  );

  return (
    <div
      className={cn("flex items-center gap-2 w-full", className)}
      role="group"
      aria-label={t("codeInput.inputGroupAriaLabel", { length: length })}
      dir={rtl ? "rtl" : "ltr"}
      {...rest}
    >
      {internalCode.map((digit, idx) => (
        <Input
          className={cn(
            "flex flex-col justify-center items-center rounded-lg border w-[4.3rem] h-[5.25rem]",
            invalid && "!outline-0 has-[:focus:not(button)]:!outline-[2px]",
          )}
          inputClassName="text-center px-0 text-neutral-500 text-xl leading-[28px] font-medium caret-primary-500 caret-[2px]"
          key={idx}
          ref={createInputRef(idx)}
          type={"text"}
          inputMode={inputType === "numeric" ? "numeric" : "text"}
          pattern={pattern}
          maxLength={1}
          value={digit === " " ? "" : digit}
          onChange={(value) => handleChange(idx, value)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          invalid={invalid}
          disabled={disabled}
          autoFocus={idx === 0 && autoFocus && !disabled}
          onPaste={(e) => handlePaste(e, idx)}
          aria-label={t("codeInput.digitInputAriaLabel", {
            index: idx + 1,
            length: length,
          })}
          aria-invalid={invalid}
        />
      ))}
    </div>
  );
};
